import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { IPage } from "api/entities/page";
import { IShardRepository, StorageDataType } from "api/entities/shard-repository";
import { IShardClusterDetailsItem } from "api/entities/shard-cluster";
import { shardRepositoryService } from "api/services/shard-repository-service";
import { DefaultRouteAction, DefaultRouteParam } from "../../../../constants/routes";
import { shardClusterThunkActions } from "redux/actions/shard-cluster";
import { IAppState } from "redux/reducers";
import { FormMode, formUtils, useField } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import { validators } from "utils/validators";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import { IShardClusterRepository, validateShardClusterRepositories } from "components/shard-clusters/sections/storage-nodes-section/shard-repositories-section";
import ShardClusterForm, { IShardClusterForm, IShardClusterFormValue, shardClusterFormDefaultValue } from "components/shard-clusters/shard-cluster-form/shard-cluster-form";
import { OwnerInfoSection } from "components/common/sections/details-owner-info/details-owner-info";
import { Formatters } from "../../../../helper/formatters";
import { shardClusterService } from "api/services/shard-cluster-service";
import { tooltipUserGuideActions } from "redux/actions/tooltip-user-guide";
import { StatusPoller } from "components/cluster/status-poller";
import styles from "./shard-cluster-panel.module.scss";

export interface IShardClusterPanelProps
  extends ReturnType<typeof mapDispatchToProps> {
  mode?: FormMode;
  embedded: boolean;
  onClose: () => void;
}

function ShardClusterPanel(props: IShardClusterPanelProps) {
  const { idOrAction } = useParams<{
    [DefaultRouteParam.IdOrAction]: string;
  }>();

  const [shardClusterApi, isShardClusterLoading] = useApi();
  const [shardRepositoryApi] = useApi();

  const open = props.embedded ? props.mode === FormMode.Create : !!idOrAction;

  const isPoolCreation = props.embedded
    ? props.mode === FormMode.Create
    : idOrAction === DefaultRouteAction.New;

  const form: IShardClusterForm = {
    name: useField<string>(shardClusterFormDefaultValue.name, [
      validators.required("Shard Cluster Name is required"),
    ]),
    shardClusterRepositories: useField<IShardClusterRepository[]>(
      shardClusterFormDefaultValue.shardClusterRepositories,
      [validateShardClusterRepositories]
    ),
    contentSecurityLevel: useField<number>(
      shardClusterFormDefaultValue.contentSecurityLevel,
      []
    ),
    keySecurityLevel: useField<number>(
      shardClusterFormDefaultValue.keySecurityLevel,
      []
    ),
    metaSecurityLevel: useField<number>(
      shardClusterFormDefaultValue.metaSecurityLevel,
      []
    ),
  };

  const [storagePool, setStoragePool] = useState<IShardClusterDetailsItem>(null);

  function close(): void {
    props.onClose();
  }

  function loadShardClusterRepositories(
    storagePool: IShardClusterDetailsItem
  ): Promise<IShardClusterRepository[]> {
    return shardRepositoryApi(
      shardRepositoryService.getPage({ pool: storagePool.id })
    ).then((shardRepositoryPage: IPage<IShardRepository>) => {
      return shardRepositoryPage.data.map((shardRepository) => ({
        shardRepository,
        types: {
          storeContent: shardRepository.types.includes(StorageDataType.Content),
          storeKey: shardRepository.types.includes(StorageDataType.Key),
          storeMetadata: shardRepository.types.includes(StorageDataType.Meta),
        },
      }));
    });
  }

  function loadStoragePool() {
    return shardClusterApi(shardClusterService.get(idOrAction)).then(
      (storagePool: IShardClusterDetailsItem) => {
        setStoragePool(storagePool);
        loadShardClusterRepositories(storagePool).then((shardClusterRepositories) => {
          formUtils.setFormValue<IShardClusterFormValue>(form, {
            name: storagePool.name,
            contentSecurityLevel: storagePool.contentSecurityLevel,
            keySecurityLevel: storagePool.keySecurityLevel,
            metaSecurityLevel: storagePool.metaSecurityLevel,
            shardClusterRepositories: shardClusterRepositories,
          });
        });
      }
    );
  }

  useEffect(() => {
    if (open) {
      if (isPoolCreation) {
        formUtils.setFormValue<IShardClusterFormValue>(
          form,
          shardClusterFormDefaultValue
        );
      } else {
        loadStoragePool();
      }
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <DetailsPanel
      isLoading={isShardClusterLoading}
      open={open}
      onCloseClick={close}
      contentClassName={styles.content}
      title={isPoolCreation ? "Add New Shard Cluster" : "Manage Shard Cluster"}
      description={
        !isPoolCreation &&
        !!storagePool && (
          <>
            <OwnerInfoSection entity={storagePool} />
            <div>
              Created At: {Formatters.formatDateTime(storagePool?.createdAt)}
            </div>
            <div>
              Deployment Status:{" "}
              <StatusPoller
                poolId={(storagePool as IShardClusterDetailsItem).id}
                className={styles.status}
              />
            </div>
          </>
        )
      }
    >
      {open && (
        <>
          {isPoolCreation && (
            <div className={styles.description}>
              Shard Cluster uses a "Shred and Spread" technique to securely
              store data by breaking it into shards and distributing them across
              multiple repositories. This configuration ensures enhanced
              security by creating an air gap between stored data and
              applications, protecting against threats like ransomware and
              malware. To ensure data confidentiality and resilience, users must
              select at least three shard repositories, providing a distributed
              and fault-tolerant object storage.
            </div>
          )}

          <ShardClusterForm
            mode={isPoolCreation ? FormMode.Create : FormMode.Edit}
            form={form}
            storagePoolId={idOrAction}
          />
        </>
      )}
    </DetailsPanel>
  );
}

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, void, Action>) {
  return {
    fetchPage: () => dispatch(shardClusterThunkActions.entity.fetchPage()),

    shardClusterTooltipOpen: () => {
      dispatch(tooltipUserGuideActions.disableStepCounter());
      dispatch(tooltipUserGuideActions.open());
      dispatch(tooltipUserGuideActions.setStep("bucket"));
    },
  };
}

export default connect(null, mapDispatchToProps)(ShardClusterPanel);
